import React from 'react';
import { TransactionStatusChip } from 'app/pages/overview/tabs/transactions/TransactionStatusChip';
import { HighnoteTransactionEvent } from 'API';
import { LabelKey, labelKeyFor, resolveStatus, StatusKey } from 'util/workflowStatus';
import { isSageImportRetryWarning } from 'app/services/agave/erpUtils';
import { useApprovalLevels } from 'app/hooks/transactions/useApprovalLevels';
import { useGroupApprovalsEnabled } from 'app/hooks/transactions/useGroupApprovalsEnabled';
import { useUserRole } from 'app/hooks/useUserRole';

type AgaveStatusChipProps = {
  erpApprovalStatus?: boolean | null;
  agaveExpenseId?: string | null;
  erpBypassStatus?: boolean | null;
  isAllowedToCode?: boolean | null;
  transaction?: HighnoteTransactionEvent | null;
  isTransactionPosting?: boolean;
  approvalLevels?: number;
};

export const getAgaveStatusText = ({
  erpApprovalStatus,
  agaveExpenseId,
  erpBypassStatus,
  transaction,
  isTransactionPosting,
  approvalLevels,
}: AgaveStatusChipProps): StatusKey | null =>
  resolveStatus({
    // The row is the source of truth, but callers still pass these individually
    // (and sometimes ahead of the row being refreshed), so they win when given.
    transaction: {
      ...(transaction ?? {}),
      ...(erpApprovalStatus !== undefined ? { erpApprovalStatus } : {}),
      ...(agaveExpenseId !== undefined ? { agaveExpenseId } : {}),
      ...(erpBypassStatus !== undefined ? { erpBypassStatus } : {}),
    } as HighnoteTransactionEvent,
    accountType: 'connected',
    isTransactionPosting,
    approvalLevels,
  });

// SEE-1861: a Sage 300 posting whose import hh2 keeps retrying reads as its own
// status. Display only — the stored status stays PENDING (see erp-post-confirm).
// Shared with mobile transaction details so both label it the same way.
export const agaveLabelKeyFor = (
  status: StatusKey,
  transaction: HighnoteTransactionEvent | null | undefined,
  approvalLevels: number,
  groupApprovalsEnabled = false
): LabelKey =>
  status === 'posting' && isSageImportRetryWarning(transaction?.erpPostingWarning)
    ? 'sageImportError'
    : labelKeyFor(status, 'connected', approvalLevels, groupApprovalsEnabled);

export const AgaveStatusChip: React.FC<AgaveStatusChipProps> = (props) => {
  const approvalLevels = useApprovalLevels('connected');
  const groupApprovalsEnabled = useGroupApprovalsEnabled();
  const { canAccessTransactions } = useUserRole();
  const status = getAgaveStatusText({ ...props, approvalLevels });
  const labelKey: LabelKey | undefined = status
    ? agaveLabelKeyFor(status, props.transaction, approvalLevels, groupApprovalsEnabled)
    : undefined;
  return (
    <TransactionStatusChip
      status={status}
      labelKey={labelKey}
      waitingOnTransactionId={canAccessTransactions ? props.transaction?.id : undefined}
    />
  );
};
